// Compatibility facade for the customer-built UI. The public API intentionally
// matches the former Base44 client while persistence and authorization now run
// through Supabase. Existing screens therefore migrate without a visual rewrite.
import { supabase } from "@/api/supabaseClient";
import { env, requireSupabaseConfig } from "@/lib/env";
import { ADMIN_ROLES, rolesForMemberships } from "@/lib/roles";
import { buildAuthCallbackUrl } from "@/lib/authReturnTo";

const PUBLIC_ENTITIES = new Set([
  "AgeGroup", "Announcement", "FAQ", "Group", "Match", "MatchEvent",
  "Package", "PackageAddon", "Pitch", "Sponsor", "Team", "Tournament",
]);
const scoreRevisions = new Map();
const RECORD_METADATA_FIELDS = new Set([
  "id", "created_at", "updated_at", "created_date", "updated_date",
  "organization_id", "tournament_id", "team_id", "participant_id",
]);

function writableChanges(changes = {}) {
  return Object.fromEntries(
    Object.entries(changes).filter(([key]) => !RECORD_METADATA_FIELDS.has(key)),
  );
}

function scorekeeperToken() {
  return sessionStorage.getItem("scorekeeper_session") || "";
}

async function applyScoreOperation(matchId, mutation, metadata = {}) {
  const sessionToken = scorekeeperToken();
  if (!sessionToken) return null;
  const expectedRevision = metadata.expectedRevision ?? scoreRevisions.get(matchId) ?? 0;
  const { data, error } = await supabase.rpc("score_apply_operation", {
    p_operation_id: metadata.operationId || crypto.randomUUID(),
    p_match_id: matchId,
    p_session_token: sessionToken,
    p_expected_revision: expectedRevision,
    p_mutation: mutation,
  });
  if (error) throw error;
  scoreRevisions.set(matchId, data.revision);
  return data;
}

const toRecord = (row) => row ? ({
  id: row.id,
  ...(row.data || {}),
  created_date: row.created_at,
  updated_date: row.updated_at,
}) : null;

function compare(actual, expected) {
  if (expected instanceof RegExp) return expected.test(String(actual ?? ""));
  if (expected && typeof expected === "object" && !Array.isArray(expected)) {
    if ("$in" in expected) return expected.$in.includes(actual);
    if ("$ne" in expected) return actual !== expected.$ne;
    if ("$gt" in expected) return actual > expected.$gt;
    if ("$gte" in expected) return actual >= expected.$gte;
    if ("$lt" in expected) return actual < expected.$lt;
    if ("$lte" in expected) return actual <= expected.$lte;
  }
  return actual === expected;
}

function matches(record, query = {}) {
  if (query.$or) return query.$or.some((branch) => matches(record, branch));
  return Object.entries(query).every(([key, value]) => key === "$or" || compare(record[key], value));
}

function sortRecords(records, sort) {
  if (!sort) return records;
  const descending = sort.startsWith("-");
  const key = descending ? sort.slice(1) : sort;
  return records.toSorted((a, b) => {
    const left = a[key] ?? "";
    const right = b[key] ?? "";
    const result = typeof left === "number" && typeof right === "number"
      ? left - right
      : String(left).localeCompare(String(right));
    return descending ? -result : result;
  });
}

async function readRows(entityType) {
  requireSupabaseConfig();
  const driverSession = sessionStorage.getItem("driver_session");
  const { data: authState } = await supabase.auth.getSession();
  const canReadPrivate = Boolean(authState.session);
  const [{ data: privateRows, error: privateError }, publicResult, driverResult] = await Promise.all([
    canReadPrivate
      ? supabase.from("entity_records").select("id,data,created_at,updated_at").eq("entity_type", entityType).limit(5000)
      : Promise.resolve({ data: [], error: null }),
    PUBLIC_ENTITIES.has(entityType)
      ? supabase.from("public_entity_records").select("id,data,created_at,updated_at").eq("entity_type", entityType).limit(5000)
      : Promise.resolve({ data: [], error: null }),
    driverSession
      ? supabase.rpc("driver_entity_list", { p_driver_session: driverSession, p_entity_type: entityType })
      : Promise.resolve({ data: [], error: null }),
  ]);
  if (privateError && !PUBLIC_ENTITIES.has(entityType)) throw privateError;
  if (publicResult.error) throw publicResult.error;
  if (driverResult.error) throw driverResult.error;

  const byId = new Map();
  for (const row of publicResult.data || []) byId.set(row.id, row);
  for (const row of driverResult.data || []) byId.set(row.id, row);
  for (const row of privateRows || []) byId.set(row.id, row);
  if (entityType === "Match") {
    for (const row of byId.values()) scoreRevisions.set(row.id, Number(row.data?.revision || 0));
  }
  return [...byId.values()];
}

async function insertRecords(entityType, payloads) {
  requireSupabaseConfig();
  const rows = payloads.map((data) => {
    const { id, ...recordData } = data;
    return {
      ...(id ? { id } : {}),
      entity_type: entityType,
      data: recordData,
    };
  });
  const { data, error } = await supabase
    .from("entity_records")
    .insert(rows)
    .select("id,data,created_at,updated_at");
  if (error) throw error;
  return (data || []).map(toRecord);
}

function createEntity(entityType) {
  return {
    async filter(query = {}, options = {}) {
      const rows = await readRows(entityType);
      const filtered = rows.map(toRecord).filter((record) => matches(record, query));
      const items = sortRecords(filtered, options.sort).slice(0, options.limit || 100);
      return { items };
    },
    async list(sort, limit = 100) {
      return this.filter({}, { sort, limit });
    },
    async get(id) {
      const rows = await readRows(entityType);
      const record = rows.find((row) => row.id === id);
      if (!record) throw new Error(`${entityType} record not found`);
      return toRecord(record);
    },
    async create(payload) {
      if (entityType === "Lead") {
        const { data, error } = await supabase.rpc("public_submit_lead", { p_fields: payload });
        if (error) throw error;
        return { id: data, ...payload };
      }
      if (entityType === "Question") {
        const { data, error } = await supabase.rpc("public_submit_question", { p_fields: payload });
        if (error) throw error;
        return { id: data, ...payload };
      }
      if (entityType === "StatEvent") {
        const { data, error } = await supabase.rpc("public_track_stat", { p_fields: payload });
        if (error) throw error;
        return { id: data, ...payload };
      }
      if (entityType === "TripChatMessage" && sessionStorage.getItem("driver_session")) {
        const { data, error } = await supabase.rpc("driver_trip_chat_send", {
          p_driver_session: sessionStorage.getItem("driver_session"),
          p_trip_id: payload.trip_id,
          p_team_id: payload.team_id,
          p_fields: payload,
        });
        if (error) throw error;
        return data;
      }
      if (entityType === "MatchEvent" && scorekeeperToken()) {
        const { _operation_id, _expected_revision, _match_changes, _score_delta, ...event } = payload;
        const result = await applyScoreOperation(event.match_id, { kind: "event_create", event, match_changes: _match_changes || {}, score_delta: _score_delta || {} }, {
          operationId: _operation_id,
          expectedRevision: _expected_revision,
        });
        return { id: result.event_id, ...event, revision: result.revision, match: result.match };
      }
      return (await insertRecords(entityType, [payload]))[0];
    },
    async bulkCreate(payloads) {
      return insertRecords(entityType, payloads);
    },
    async update(id, changes) {
      requireSupabaseConfig();
      if (entityType === "Match" && scorekeeperToken()) {
        const { _operation_id, _expected_revision, ...matchChanges } = changes;
        const result = await applyScoreOperation(id, { kind: "match_patch", changes: matchChanges }, {
          operationId: _operation_id,
          expectedRevision: _expected_revision,
        });
        return result.match;
      }
      if (entityType === "MatchEvent" && scorekeeperToken()) {
        const existing = await this.get(id);
        if (changes.deleted) {
          const result = await applyScoreOperation(existing.match_id, { kind: "event_delete", event_id: id, match_changes: changes._match_changes || {}, score_delta: changes._score_delta || {} }, {
            operationId: changes._operation_id,
            expectedRevision: changes._expected_revision,
          });
          return { ...existing, ...changes, revision: result.revision, match: result.match };
        }
        throw new Error("Scorekeepers can only remove existing match events.");
      }
      const { data, error } = await supabase.rpc("entity_update", {
        p_entity_type: entityType,
        p_id: id,
        // Screens built against Base44 commonly submit the full loaded record.
        // Scope and row metadata are server controlled and must never be echoed
        // back as editable JSON fields.
        p_changes: writableChanges(changes),
      });
      if (error) throw error;
      return toRecord(data);
    },
    async delete(id) {
      requireSupabaseConfig();
      const { error } = await supabase.from("entity_records").delete().eq("entity_type", entityType).eq("id", id);
      if (error) throw error;
      return true;
    },
    async count(query = {}) {
      const { items } = await this.filter(query, { limit: 5000 });
      return items.length;
    },
    async aggregate({ groupBy, sum } = {}) {
      const { items } = await this.filter({}, { limit: 5000 });
      const groups = new Map();
      for (const item of items) {
        const key = item[groupBy] ?? null;
        const row = groups.get(key) || { [groupBy]: key, count: 0, sum: 0 };
        row.count += 1;
        if (sum) row.sum += Number(item[sum] || 0);
        groups.set(key, row);
      }
      return { rows: [...groups.values()] };
    },
    subscribe(callback) {
      if (!env.isSupabaseConfigured) return () => {};
      const channel = supabase
        .channel(`entity:${entityType}:${crypto.randomUUID()}`)
        .on("postgres_changes", {
          event: "*",
          schema: "public",
          table: "entity_records",
          filter: `entity_type=eq.${entityType}`,
        }, callback)
        .subscribe();
      return () => { supabase.removeChannel(channel); };
    },
  };
}

const entities = new Proxy({}, {
  get(target, entityType) {
    if (typeof entityType !== "string") return undefined;
    if (!target[entityType]) target[entityType] = createEntity(entityType);
    return target[entityType];
  },
});

async function currentUser() {
  requireSupabaseConfig();
  const { data: authData, error: authError } = await supabase.auth.getUser();
  if (authError || !authData.user) throw authError || new Error("Authentication required");

  const [profileResult, orgResult, tournamentResult, teamResult, guardianResult] = await Promise.all([
    supabase.from("profiles").select("id,email,full_name,disabled_at").eq("id", authData.user.id).maybeSingle(),
    supabase.from("organization_memberships").select("organization_id,role,status").eq("user_id", authData.user.id).eq("status", "active"),
    supabase.from("tournament_assignments").select("tournament_id,role,scope").eq("user_id", authData.user.id).eq("active", true),
    supabase.from("team_memberships").select("team_id,role,status").eq("user_id", authData.user.id).eq("status", "active"),
    supabase.from("guardian_links").select("participant_id").eq("user_id", authData.user.id).not("verified_at", "is", null).is("revoked_at", null).limit(1),
  ]);
  for (const result of [profileResult, orgResult, tournamentResult, teamResult, guardianResult]) {
    if (result.error) throw result.error;
  }
  const profile = profileResult.data;
  const orgMemberships = orgResult.data;
  const tournamentAssignments = tournamentResult.data;
  const teamMemberships = teamResult.data;
  const guardianLinks = guardianResult.data;
  if (!profile) throw new Error("Your Matchbase account profile has not been provisioned. Contact an administrator.");
  if (profile?.disabled_at) throw new Error("This account has been disabled. Contact an administrator.");

  const roles = rolesForMemberships({
    organization: orgMemberships || [],
    tournament: tournamentAssignments || [],
    team: teamMemberships || [],
    guardian: guardianLinks || [],
  });
  const legacyRole = roles.some((role) => ADMIN_ROLES.includes(role)) ? "admin" : "user";
  return {
    id: authData.user.id,
    email: authData.user.email,
    full_name: profile?.full_name || authData.user.user_metadata?.full_name || "",
    role: legacyRole,
    roles,
    organization_memberships: orgMemberships || [],
    tournament_assignments: tournamentAssignments || [],
    team_memberships: teamMemberships || [],
    guardian_links: guardianLinks || [],
  };
}

function authRedirect(path = "/") {
  return buildAuthCallbackUrl(path, env.appUrl);
}

const auth = {
  me: currentUser,
  async loginViaEmailPassword(email, password, returnTo = "/") {
    requireSupabaseConfig();
    if (password) {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      return { ...data, magicLinkSent: false, returnTo };
    }
    const { data, error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: authRedirect(returnTo), shouldCreateUser: false },
    });
    if (error) throw error;
    return { ...data, magicLinkSent: true };
  },
  async register({ email, returnTo = "/" }) {
    requireSupabaseConfig();
    const { data, error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: authRedirect(returnTo), shouldCreateUser: true },
    });
    if (error) throw error;
    return { ...data, magicLinkSent: true };
  },
  async verifyOtp({ email, otpCode }) {
    requireSupabaseConfig();
    const { data, error } = await supabase.auth.verifyOtp({ email, token: otpCode, type: "email" });
    if (error) throw error;
    return { ...data.session, access_token: data.session?.access_token };
  },
  async resendOtp(email, returnTo = "/") {
    return this.register({ email, returnTo });
  },
  async loginWithProvider(provider, returnTo = "/") {
    requireSupabaseConfig();
    if (provider !== "google") throw new Error("Unsupported sign-in provider");
    const { error } = await supabase.auth.signInWithOAuth({ provider, options: { redirectTo: authRedirect(returnTo) } });
    if (error) throw error;
  },
  async resetPasswordRequest(email, returnTo = "/") {
    return this.loginViaEmailPassword(email, null, returnTo);
  },
  async resetPassword({ newPassword }) {
    requireSupabaseConfig();
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
  },
  setToken() {},
  async logout(redirectTo) {
    await supabase.auth.signOut();
    if (redirectTo) window.location.assign("/login");
  },
  redirectToLogin(returnTo = window.location.href) {
    const target = new URL("/login", window.location.origin);
    const parsed = new URL(returnTo, window.location.origin);
    if (parsed.origin === window.location.origin) target.searchParams.set("returnTo", parsed.pathname + parsed.search);
    window.location.assign(target.toString());
  },
};

async function uploadPrivateFile(file, scope = {}) {
  requireSupabaseConfig();
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) throw new Error("Authentication required");
  const [{ data: orgMemberships }, { data: teamMemberships }] = await Promise.all([
    supabase.from("organization_memberships").select("organization_id").eq("user_id", userData.user.id).eq("status", "active").limit(1),
    supabase.from("team_memberships").select("team_id").eq("user_id", userData.user.id).eq("status", "active").limit(1),
  ]);
  const teamId = scope.team_id || teamMemberships?.[0]?.team_id || null;
  let organizationId = scope.organization_id || orgMemberships?.[0]?.organization_id || null;
  let tournamentId = scope.tournament_id || null;
  if (teamId) {
    const { data: team } = await supabase.from("entity_records").select("organization_id,tournament_id").eq("id", teamId).eq("entity_type", "Team").maybeSingle();
    organizationId ||= team?.organization_id || null;
    tournamentId = team?.tournament_id || null;
  }
  if (!organizationId) throw new Error("Your account is not assigned to an organization or team.");
  const cleanName = file.name.replace(/[^a-zA-Z0-9._-]/g, "-");
  const path = `${userData.user.id}/${crypto.randomUUID()}-${cleanName}`;
  const { error } = await supabase.storage.from("private-documents").upload(path, file, { upsert: false });
  if (error) throw error;
  const retention = new Date();
  retention.setUTCDate(retention.getUTCDate() + 60);
  const { error: metadataError } = await supabase.from("private_files").insert({
    organization_id: organizationId,
    tournament_id: tournamentId,
    team_id: teamId,
    participant_id: scope.participant_id || null,
    bucket_id: "private-documents",
    object_path: path,
    purpose: scope.purpose || "other",
    original_name: file.name,
    media_type: file.type || null,
    size_bytes: file.size,
    retention_until: retention.toISOString(),
  });
  if (metadataError) {
    await supabase.storage.from("private-documents").remove([path]);
    throw metadataError;
  }
  return { file_uri: `private-documents/${path}` };
}

async function uploadPublicAsset(file) {
  requireSupabaseConfig();
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) throw new Error("Authentication required");
  if (!file.type.startsWith("image/")) throw new Error("Only images may be uploaded as public assets.");
  const cleanName = file.name.replace(/[^a-zA-Z0-9._-]/g, "-");
  const path = `${userData.user.id}/${crypto.randomUUID()}-${cleanName}`;
  const { error } = await supabase.storage.from("public-assets").upload(path, file, {
    cacheControl: "31536000",
    upsert: false,
  });
  if (error) throw error;
  const { data } = supabase.storage.from("public-assets").getPublicUrl(path);
  return { file_url: data.publicUrl };
}

async function createSignedUrl({ file_uri, expires_in = 300 }) {
  requireSupabaseConfig();
  const { data, error } = await supabase.functions.invoke("secure-operations", {
    body: { action: "private_file_link", file_uri, expires_in: Math.min(expires_in, 300) },
  });
  if (error) throw error;
  return { signed_url: data.signed_url };
}

async function inviteTeamMember({ team_id, email }) {
  requireSupabaseConfig();
  const { data, error } = await supabase.functions.invoke("secure-operations", {
    body: { action: "invite_team_member", team_id, email },
  });
  if (error) throw error;
  return data;
}

async function cancelTeamRegistration(registrationId) {
  requireSupabaseConfig();
  const { data, error } = await supabase.rpc("team_registration_cancel", { p_registration_id: registrationId });
  if (error) throw error;
  return data;
}

async function setPitchPin(pitchId, pin) {
  requireSupabaseConfig();
  const { data, error } = await supabase.rpc("pitch_pin_set", { p_pitch_id: pitchId, p_pin: pin });
  if (error) throw error;
  return data;
}

async function getScorekeeperSessionStatus(tournamentId) {
  requireSupabaseConfig();
  const { data, error } = await supabase.rpc("scorekeeper_session_status", { p_tournament_id: tournamentId });
  if (error) throw error;
  return data || [];
}

async function createTournament(fields) {
  requireSupabaseConfig();
  const { data, error } = await supabase.rpc("tournament_create", { p_fields: fields });
  if (error) throw error;
  return data;
}

async function getPublicTournamentSnapshot(slug) {
  requireSupabaseConfig();
  if (import.meta.env.PROD && !["localhost", "127.0.0.1"].includes(window.location.hostname)) {
    const response = await fetch(`/api/public-tournament?slug=${encodeURIComponent(slug)}`, {
      headers: { accept: "application/json" },
    });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error("Tournament data is temporarily unavailable");
    return response.json();
  }
  const { data, error } = await supabase.rpc("public_tournament_snapshot", { p_slug: slug });
  if (error) throw error;
  return data;
}

export const base44 = {
  auth,
  entities,
  operations: { cancelTeamRegistration, setPitchPin, getScorekeeperSessionStatus, createTournament },
  public: { getTournamentSnapshot: getPublicTournamentSnapshot },
  app: { async getPublicSettings() { return { id: "matchbase", public_settings: { backend: "supabase" } }; } },
  integrations: {
    Core: {
      UploadPrivateFile: ({ file, ...scope }) => uploadPrivateFile(file, scope),
      UploadFile: ({ file }) => uploadPublicAsset(file),
      CreateFileSignedUrl: createSignedUrl,
      InviteTeamMember: inviteTeamMember,
    },
  },
};
